import { useCallback, useState } from 'react';

/**
 * Alerts the user has marked as seen in the attention centre.
 *
 * **These are open critical incidents, not messages**, so dismissing one must
 * never make a real problem disappear. Three things follow from that, and each
 * is load-bearing:
 *
 *  1. A dismissal is keyed to the incident AND its last event. Any new activity
 *     produces a new key, so an incident that escalates comes straight back
 *     rather than staying hidden because someone glanced at it yesterday.
 *  2. Dismissing changes this list only. The incident stays open, stays in the
 *     queue, and stays on the dashboard.
 *  3. Nothing is hidden irrecoverably: the panel says how many are dismissed
 *     and offers to show them.
 *
 * Held in `localStorage` rather than on the server because "I have seen this"
 * is per-person view state, like the saved table views. The trade-off is that
 * it does not follow a user to another browser — which errs towards showing an
 * alert again, the safe direction.
 */
const KEY = 'oip.dismissedAlerts';

/** Bounded so a long-lived browser cannot grow the entry without limit. */
const MAX_REMEMBERED = 200;

/** Identity of one alert AT A POINT IN ITS LIFE. A new event means a new key. */
export function alertKey(id: string, lastEventAt: string): string {
  return `${id}@${lastEventAt}`;
}

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    // A corrupt entry must not take the shell down; the worst case of ignoring
    // it is that a few alerts reappear.
    return [];
  }
}

export interface DismissedAlerts {
  isDismissed: (id: string, lastEventAt: string) => boolean;
  dismiss: (keys: Array<{ id: string; lastEventAt: string }>) => void;
  restoreAll: () => void;
  count: number;
}

export function useDismissedAlerts(): DismissedAlerts {
  const [keys, setKeys] = useState<string[]>(read);

  const persist = useCallback((next: string[]) => {
    // Newest kept: an old dismissal matters least, and its incident will
    // reappear rather than being lost.
    const capped = next.slice(-MAX_REMEMBERED);
    setKeys(capped);
    try {
      localStorage.setItem(KEY, JSON.stringify(capped));
    } catch {
      // Storage full or blocked. The dismissal still applies for this session.
    }
  }, []);

  return {
    isDismissed: useCallback((id, lastEventAt) => keys.includes(alertKey(id, lastEventAt)), [keys]),
    dismiss: useCallback(
      (items) => {
        const next = new Set(keys);
        for (const i of items) next.add(alertKey(i.id, i.lastEventAt));
        persist([...next]);
      },
      [keys, persist],
    ),
    restoreAll: useCallback(() => persist([]), [persist]),
    count: keys.length,
  };
}
